/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { Observable, from, firstValueFrom, BehaviorSubject } from 'rxjs';
import { getLoginRequest, getGraphConfig } from '../../../auth-config';
import { ConfigService } from '../config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStatusSubject = new BehaviorSubject<boolean>(false);
  public authStatus$ = this.authStatusSubject.asObservable();

  constructor(
    private msalService: MsalService,
    private configService: ConfigService
  ) {
    // Initialize auth status
    this.updateAuthStatus();
  }

  login(): void {
    const loginRequest = getLoginRequest(this.configService);
    this.msalService.loginRedirect(loginRequest);
  }

  logout(): void {
    try {
      // Clear active account
      this.msalService.instance.setActiveAccount(null);

      // Clear all cached tokens and accounts
      this.msalService.instance.clearCache();

      // Update auth status
      this.updateAuthStatus();

      console.log('User logged out locally - no redirect to Microsoft');
    } catch (error) {
      console.error('Error during logout:', error);
      // Ensure we still update the auth status even if logout fails
      this.updateAuthStatus();
    }
  }

  logoutWithRedirect(): void {
    // Full logout with redirect to Microsoft (if needed)
    this.msalService.logoutRedirect();
  }

  getAccount(): AccountInfo | null {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  isLoggedIn(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  private updateAuthStatus(): void {
    const isAuthenticated = this.isLoggedIn();
    this.authStatusSubject.next(isAuthenticated);
  }

  public refreshAuthStatus(): void {
    this.updateAuthStatus();
  }

  getAccessToken(): Observable<string> {
    return from(this.getAccessTokenAsync());
  }

  private async getAccessTokenAsync(): Promise<string> {
    const account = this.getAccount();
    if (account) {
      const graphConfig = getGraphConfig(this.configService);
      const accessTokenRequest = {
        scopes: graphConfig.graphScopes,
        account: account
      };
      try {
        const response = await this.msalService.instance.acquireTokenSilent(accessTokenRequest);
        return response.accessToken;
      } catch (error) {
        this.msalService.instance.acquireTokenRedirect(accessTokenRequest);
        return '';
      }
    }
    return '';
  }

  async getUserInfo(): Promise<any> {
    try {
      const token = await firstValueFrom(this.getAccessToken());
      if (token) {
        const graphConfig = getGraphConfig(this.configService);
        const response = await fetch(graphConfig.graphMeEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        return response.json();
      }
      return null;
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  }
}
